import { nameof } from "ts-simple-nameof";
import { useUserWizardPage } from "../hooks/useUserWizardPage";
import { PageMode } from "../../../../../common/types";
import { HiddenField } from "../../../../../common/components/forms/inputs/hiddenField/HiddenField";
import { IUserWizardFormModel } from "../models/userWizardFormModel";
import { DefaultInputField } from "../../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultPhoneInputField } from "../../../../../common/components/forms/inputs/phoneField/DefaultPhoneInputField";
import { DefaultButton } from "../../../../../common/components/button/defaultButton/DefaultButton";
import { FormProvider, SubmitHandler } from "react-hook-form";
import styles from "./UserWizardContents.module.scss";
import { RenderWhen } from "../../../../../common/infrastructure/components/RenderWhen";
import { BirthDateField } from "../../../../../common/components/forms/inputs/birthDateField/BirthDateField";
import { DefaultSliderField } from "../../../../../common/components/forms/inputs/sliderField/DefaultSliderField";
import { useAppContext } from "../../../../../AppContext";

interface IUserWizardContentsProps {
  pageMode: PageMode;
  onCreated?: (newUserId: number, newUserName: string) => void;
  hideAdditionalInfo?: boolean;
  hideFontSize?: boolean;
  hideScheduleVisibility?: boolean;
  portalFamilyMode?: boolean;
  showBirthDate?: boolean;
  customIsLoading?: boolean;
  additionalHandleSubmit?: SubmitHandler<IUserWizardFormModel>;
  customDisclaimerText?: string;
}

export const UserWizardContents = ({
  pageMode,
  onCreated,
  hideAdditionalInfo,
  hideFontSize,
  hideScheduleVisibility,
  portalFamilyMode,
  showBirthDate,
  customIsLoading = false,
  additionalHandleSubmit,
  customDisclaimerText,
}: IUserWizardContentsProps) => {
  const {
    form,
    isLoading,
    actionButtonText,
    client,
    isCreateRequestLoading,
    isDesktopOrTablet,
    handleSubmit,
  } = useUserWizardPage(pageMode, onCreated);

  const { showGroupTypeOptions, showServicesTypeOptions, showGymTypeOptions } =
    useAppContext();
  const isGymOnly =
    showGymTypeOptions && !showGroupTypeOptions && !showServicesTypeOptions;

  const children = (
    <div className={styles.root_fields}>
      <HiddenField name={nameof<IUserWizardFormModel>((x) => x.id)} />
      <HiddenField name={nameof<IUserWizardFormModel>((x) => x.studioId)} />

      <DefaultInputField
        name={nameof<IUserWizardFormModel>((x) => x.firstName)}
        label="Име"
        placeholder="Николай"
        required
      />

      <DefaultInputField
        name={nameof<IUserWizardFormModel>((x) => x.lastName)}
        label="Фамилно име"
        placeholder="Николаев"
        required
      />

      <RenderWhen condition={!hideAdditionalInfo && !portalFamilyMode}>
        <DefaultInputField
          name={nameof<IUserWizardFormModel>((x) => x.additionalInfo)}
          label="Допълнителна информация"
          placeholder="Име на родител"
        />
      </RenderWhen>

      <DefaultPhoneInputField
        label="Телефонен номер"
        name={nameof<IUserWizardFormModel>((x) => x.phoneNumber)}
      />

      <DefaultInputField
        name={nameof<IUserWizardFormModel>((x) => x.email)}
        label="Имейл"
        placeholder="example@mail.com"
      />

      <RenderWhen condition={!portalFamilyMode || showBirthDate}>
        <BirthDateField
          name={nameof<IUserWizardFormModel>((x) => x.birthDate)}
          label="Дата на раждане"
        />
      </RenderWhen>

      <RenderWhen
        condition={!portalFamilyMode && !isGymOnly && !hideScheduleVisibility}
      >
        <DefaultInputField
          name={nameof<IUserWizardFormModel>(
            (x) => x.scheduleVisibilityDaysAhead,
          )}
          label="Колко дни напред да вижда графика потребителя?"
          tooltip="Имайте предивд, че ако това поле има стойност, независимо дали потребителя има активен план или не, той ще вижда дадения брой дни напред в календара спрямо тази стойност."
          placeholder="60"
          type="number"
        />
      </RenderWhen>

      <RenderWhen condition={!portalFamilyMode}>
        <DefaultInputField
          name={nameof<IUserWizardFormModel>((x) => x.rfidBandId)}
          label="RFID / NFC карта или гривна"
          placeholder="Сканирай карта тук или въведи ръчно"
          tooltip="Уникален идентификатор на картата за влизане в залата. Може да се сканира директно в полето, ако има включен USB четец."
        />
      </RenderWhen>

      <RenderWhen condition={!portalFamilyMode && !hideFontSize}>
        <DefaultSliderField
          name={nameof<IUserWizardFormModel>((x) => x.fontSizePercent)}
          label="Размер на шрифта"
          min={80}
          max={140}
          step={5}
          valueSuffix="%"
          tooltip="Променете размера на шрифта в приложението. 100% е стандартният размер."
        />
      </RenderWhen>

      <div className={styles.root_buttons}>
        <DefaultButton
          label={actionButtonText}
          type="button"
          onClick={form.handleSubmit(additionalHandleSubmit ?? handleSubmit)}
          loading={!!isCreateRequestLoading || isLoading || customIsLoading}
        />

        <RenderWhen condition={!!customDisclaimerText}>
          <span
            className={styles.root_disclaimer}
            style={{ width: isDesktopOrTablet ? "600px" : "80vw" }}
          >
            {customDisclaimerText}
          </span>
        </RenderWhen>
      </div>
    </div>
  );

  return <FormProvider {...form}>{children}</FormProvider>;
};
